import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import Button from "@mui/material/Button";

const WheelOfPrizes = forwardRef(function WheelOfPrizes(
  {
    segments,
    size = 340,
    spinDuration = 5000, // legacy immediate-spin duration
    spins = 6, // legacy immediate-spin turns
    onFinish,
    buttonLabel = "Spin",
    wedgeRadius = 1, // 0..1 : how "tall" wedges are (radius)
    labelMaxCharsPerLine = 10, // wrapping width for labels

    // backend-driven spin:
    pendingSpeed = 480, // deg/sec while waiting for backend
    settleDuration = 4500, // ms to decelerate after backend responds
    extraTurns = 4, // extra full turns in settle phase
  },
  ref
) {
  const N = segments.length;
  const segAngle = 360 / Math.max(1, N);

  // ---- state ----
  const [rot, setRot] = useState(0); // absolute rotation (deg)
  const [spinning, setSpinning] = useState(false); // for button disabled
  const finishTimer = useRef(null);

  // phases & animation control
  const [phase, setPhase] = useState("idle"); // "idle" | "pending" | "settling"
  const phaseRef = useRef(phase);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const [transitionMs, setTransitionMs] = useState(0);
  const rafRef = useRef(null);
  const lastTsRef = useRef(null);

  // keep current rotation in a ref to avoid stale closure during pending phase
  const rotRef = useRef(0);
  useEffect(() => {
    rotRef.current = rot;
  }, [rot]);

  // pointer is at the TOP of the wheel (12 o’clock)
  const POINTER_ANGLE = -90; // degrees

  // ---- geometry (unchanged UI) ----
  const ringInset = size * 0.04;
  const wheelInset = size * 0.07;
  const localSize = size - 2 * wheelInset;
  const radius = localSize / 2;
  const cx = radius;
  const cy = radius;

  const innerR = radius * Math.max(0.2, Math.min(1, wedgeRadius));
  const base = -90;

  const polarToXY = (r, angleDeg, x0 = cx, y0 = cy) => {
    const a = (angleDeg * Math.PI) / 180;
    return [x0 + r * Math.cos(a), y0 + r * Math.sin(a)];
  };

  const wrapLabel = (text, max = labelMaxCharsPerLine) => {
    if (!text) return [""];
    const words = String(text).split(/\s+/);
    const lines = [];
    let current = "";
    for (const w of words) {
      if ((current + " " + w).trim().length <= max) {
        current = (current ? current + " " : "") + w;
      } else {
        if (current) lines.push(current);
        current = w;
      }
    }
    if (current) lines.push(current);
    if (lines.length > 2) {
      lines.splice(
        1,
        lines.length - 2,
        words
          .slice(lines[0].split(" ").length)
          .join(" ")
          .slice(0, max + 6) + "…"
      );
    }
    return lines;
  };

  const arcs = useMemo(() => {
    return segments.map((s, i) => {
      const startAng = base + i * segAngle;
      const endAng = base + (i + 1) * segAngle;
      const midAngle = startAng + segAngle / 2;

      const [x1, y1] = polarToXY(innerR, startAng);
      const [x2, y2] = polarToXY(innerR, endAng);
      const largeArc = segAngle > 180 ? 1 : 0;

      const path = [
        `M ${cx} ${cy}`,
        `L ${x1} ${y1}`,
        `A ${innerR} ${innerR} 0 ${largeArc} 1 ${x2} ${y2}`,
        "Z",
      ].join(" ");

      return { ...s, i, path, midAngle };
    });
  }, [segments, segAngle, innerR, cx, cy]);

  // ---------- rotation helpers ----------
  // lands the chosen segment's MID exactly under the top pointer
  const rotationToLandIndexImmediate = (index) => {
    const mid = base + index * segAngle + segAngle / 2;
    // add N full spins, then align to top pointer
    return spins * 360 + (POINTER_ANGLE - mid);
  };

  const rotationToLandIndexSettle = (index, fromRot) => {
    const mid = base + index * segAngle + segAngle / 2;
    const baseLanding = POINTER_ANGLE - mid; // landing with 0 extra turns
    // ensure at least `extraTurns` more full rotations from *current* rotation
    const needed = fromRot + extraTurns * 360;
    const k = Math.ceil((needed - baseLanding) / 360);
    return k * 360 + baseLanding;
  };

  // ---------- two-phase spin API ----------
  const startSpin = () => {
    if (!segments?.length || phaseRef.current !== "idle") return;
    setPhase("pending");
    setSpinning(true);
    setTransitionMs(0);
    lastTsRef.current = null;

    const loop = (ts) => {
      if (phaseRef.current !== "pending") return; // read from ref
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;
      setRot((r) => r + pendingSpeed * dt);
      rafRef.current = requestAnimationFrame(loop);
    };

    requestAnimationFrame(loop); // kick after setting state
  };

  const resolveSpin = (targetCode) => {
    if (!segments?.length) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;

    let index = segments.findIndex((s) => s.code === targetCode);
    if (index < 0) index = Math.floor(Math.random() * segments.length);

    const fromRot = rotRef.current; // <— use the latest rotation
    const finalRot = rotationToLandIndexSettle(index, fromRot);

    setPhase("settling");
    setTransitionMs(settleDuration);
    setRot(finalRot);

    clearTimeout(finishTimer.current);
    finishTimer.current = setTimeout(() => {
      setPhase("idle");
      setTransitionMs(0);
      setSpinning(false);
      onFinish?.(segments[index]);
    }, settleDuration + 30);
  };

  // ---------- legacy single-call spin (kept for your built-in button) ----------
  const spinInternal = (targetCode) => {
    if (spinning || N === 0) return;
    setSpinning(true);

    let index =
      targetCode != null
        ? segments.findIndex((s) => s.code === targetCode)
        : Math.floor(Math.random() * N);
    if (index < 0) index = Math.floor(Math.random() * N);

    const norm = ((rotRef.current % 360) + 360) % 360; // <— use rotRef
    const finalRot = norm + rotationToLandIndexImmediate(index);

    setPhase("settling");
    setTransitionMs(spinDuration);
    setRot(finalRot);

    clearTimeout(finishTimer.current);
    finishTimer.current = setTimeout(() => {
      setPhase("idle");
      setTransitionMs(0);
      setSpinning(false);
      onFinish?.(segments[index]);
    }, spinDuration + 50);
  };

  useImperativeHandle(ref, () => ({
    startSpin,
    resolveSpin,
    spin: spinInternal, // legacy helper
  }));

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearTimeout(finishTimer.current);
    };
  }, []);

  return (
    <div className="w-full flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Outer blue rim (flat) */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: "conic-gradient(#1d4ed8, #2563eb 40%, #1d4ed8 100%)",
          }}
        />

        {/* Dark ring */}
        <div
          className="absolute rounded-full"
          style={{ inset: size * 0.04, background: "#0f172a" }}
        />

        {/* Spinning wheel (all geometry uses localSize coords) */}
        <div
          className="absolute"
          style={{
            inset: size * 0.07,
            width: localSize,
            height: localSize,
            transform: `rotate(${rot}deg)`,
            transition: transitionMs
              ? `transform ${transitionMs}ms cubic-bezier(0.17, 0.88, 0.3, 1.01)`
              : "none",
            borderRadius: "50%",
            overflow: "hidden",
            background: "#0b1020",
          }}
        >
          <svg
            width={localSize}
            height={localSize}
            viewBox={`0 0 ${localSize} ${localSize}`}
          >
            {/* wedges (no stroke -> no visual width bias) */}
            {arcs.map((seg) => (
              <path key={seg.i} d={seg.path} fill={seg.color} />
            ))}

            {/* labels (unchanged UI) */}
            {arcs.map((seg) => {
              let rotText = seg.midAngle - 90;
              rotText += 180;

              const lines = wrapLabel(seg.name);

              const labelR = innerR * 0.72;
              const [tx, ty] = polarToXY(labelR, seg.midAngle);

              const segAngleRad = (segAngle * Math.PI) / 180;
              const maxWidth = 2 * labelR * Math.sin(segAngleRad / 2) * 0.85;

              const K = 0.6;
              let fs = Math.max(14, Math.floor(localSize * 0.045));
              const fits = (sizePx) =>
                lines.every(
                  (line) => (line?.length || 0) * (K * sizePx) <= maxWidth
                );

              while (!fits(fs) && fs > 10) fs -= 1;
              const lineHeight = fs;

              return (
                <g
                  key={`t-${seg.i}`}
                  transform={`translate(${tx}, ${ty}) rotate(${rotText})`}
                >
                  <text
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={seg?.code === "try" ? "#000000" : "#ffffff"}
                    fontWeight="700"
                    style={{
                      fontFamily:
                        "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
                    }}
                    fontSize={fs}
                  >
                    <tspan
                      x="0"
                      dy={lines.length === 1 ? 0 : -0.6 * lineHeight}
                    >
                      {lines[0]}
                    </tspan>
                    {lines[1] && (
                      <tspan x="0" dy={lineHeight}>
                        {lines[1]}
                      </tspan>
                    )}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* hub */}
        <div
          className="absolute rounded-full bg-white border border-[#0f172a]"
          style={{
            width: size * 0.12,
            height: size * 0.12,
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />

        {/* pointer (attached to hub) */}
        <div
          className="absolute left-1/2"
          style={{
            top: "50%",
            transform: `translate(-50%, calc(-50% - ${size * 0.12 * 0.62}px))`,
          }}
        >
          <div
            style={{
              width: 0,
              height: 0,
              borderLeft: `${size * 0.02}px solid transparent`,
              borderRight: `${size * 0.02}px solid transparent`,
              borderBottom: `${size * 0.045}px solid white`,
            }}
          />
        </div>
      </div>

      {/* convenience button (unchanged UI) */}
      {/* <Button
        variant="contained"
        disabled={spinning || !N}
        onClick={() => spinInternal()}
        sx={{
          mt: 2,
          px: 4,
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 700,
          background: "linear-gradient(90deg,#2563eb,#1d4ed8)",
          ":hover": { background: "linear-gradient(90deg,#1e50d5,#1a44c2)" },
        }}
      >
        {buttonLabel}
      </Button> */}
    </div>
  );
});

export default WheelOfPrizes;
